import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Ban } from "lucide-react";
import { api, brl, ORDER_STATUS, PAY_STATUS, useSeo } from "@/lib/api";
import { PixBox, Timeline, OrderItems, PaymentBrick } from "@/components/store/OrderParts";

const VIEW = {
  aprovado: [CheckCircle2, "Pagamento aprovado!", "Recebemos seu pagamento. Em breve seu pedido entra em produção/separação.", "text-[#00C8FF]"],
  pendente: [Clock, "Pagamento pendente", "Assim que o pagamento for confirmado, atualizaremos seu pedido automaticamente.", "text-yellow-400"],
  em_processamento: [Clock, "Pagamento em processamento", "Estamos aguardando a confirmação do Mercado Pago.", "text-yellow-400"],
  recusado: [XCircle, "Pagamento recusado", "Não foi possível aprovar o pagamento. Tente outro meio abaixo.", "text-red-400"],
  cancelado: [Ban, "Pedido cancelado", "Este pedido foi cancelado.", "text-red-400"],
};

export default function OrderSuccess() {
  const [sp] = useSearchParams();
  const id = sp.get("id"), token = sp.get("token");
  const [o, setO] = useState(null);
  const [err, setErr] = useState(false);
  const [retry, setRetry] = useState(false);
  const brickOpen = useRef(false);
  useSeo({ title: "Status do pedido" });
  const brickOrder = useMemo(() => (o ? { id: o.id, total: o.total, token, email: o.customer.email } : null), [o?.id, o?.total, token, o?.customer?.email]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let t;
    const load = () => api.get(`/orders/public/${id}`, { params: { token } }).then((r) => {
      setO(r.data);
      if (!r.data.paid && r.data.status !== "cancelado") t = setTimeout(load, brickOpen.current ? 20000 : 6000);
    }).catch(() => setErr(true));
    load();
    return () => clearTimeout(t);
  }, [id, token]);
  if (err) return <div className="max-w-xl mx-auto py-32 text-center"><h1 className="text-2xl font-bold">Pedido não encontrado</h1></div>;
  if (!o) return <div className="max-w-xl mx-auto py-32"><div className="h-64 rounded-3xl bg-[#171A1F] animate-pulse" /></div>;
  const ps = o.status === "cancelado" ? "cancelado" : o.paid ? "aprovado" : o.payment_status || "pendente";
  const [I, title, text, color] = VIEW[ps] || VIEW.pendente;
  const canPay = !o.paid && o.status !== "cancelado" && (!o.payment?.qr_code || ps === "recusado");
  brickOpen.current = canPay && (retry || !o.payment?.provider);
  return (
    <div className="max-w-[1100px] mx-auto px-4 lg:px-8 py-12" data-testid="order-status-page">
      <div className="text-center mb-10">
        <I className={`h-14 w-14 mx-auto ${color}`} />
        <h1 className="text-3xl lg:text-4xl font-bold mt-4" data-testid="payment-status-title">{title}</h1>
        <p className="text-[#A0A0A0] mt-2 max-w-lg mx-auto">{text}</p>
        <p className="mt-4 font-mono text-sm" data-testid="order-number">Pedido {o.number}</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          {!o.paid && <PixBox pay={o.payment} />}
          {!o.paid && o.payment?.ticket_url && !o.payment?.qr_code && <a href={o.payment.ticket_url} target="_blank" rel="noreferrer" className="btn-primary w-full" data-testid="boleto-link">Abrir boleto</a>}
          {canPay && (retry || !o.payment?.provider ? <div className="card p-6"><PaymentBrick order={brickOrder} onPaid={(n) => { setO(n); setRetry(false); }} /></div>
            : <button className="btn-primary w-full" onClick={() => setRetry(true)} data-testid="retry-payment">Tentar pagar novamente</button>)}
          <div className="card p-6"><h3 className="font-semibold mb-5">Acompanhamento</h3><Timeline order={o} /></div>
        </div>
        <div className="card p-6 h-fit space-y-4">
          <div className="flex justify-between text-sm"><span className="text-[#A0A0A0]">Status</span><b data-testid="order-status">{ORDER_STATUS[o.status]}</b></div>
          <div className="flex justify-between text-sm"><span className="text-[#A0A0A0]">Pagamento</span><b>{PAY_STATUS[o.payment_status]}</b></div>
          <OrderItems order={o} />
          <div className="border-t border-[#282D37] pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-[#A0A0A0]">Frete ({o.shipping_method?.name})</span><span>{brl(o.shipping)}</span></div>
            {o.discount + o.pix_discount > 0 && <div className="flex justify-between text-[#00C8FF]"><span>Descontos</span><span>-{brl(o.discount + o.pix_discount)}</span></div>}
            <div className="flex justify-between text-lg font-bold"><span>Total</span><span>{brl(o.total)}</span></div>
          </div>
          <div className="text-xs text-[#A0A0A0]">Próximos passos: {o.items.some((i) => i.stock_type === "sob_encomenda") ? `após a confirmação do pagamento, seu pedido entra na fila de produção (até ${o.production_days} ${o.production_days === 1 ? "dia útil" : "dias úteis"}) e depois segue para envio.` : "após a confirmação do pagamento, seu pedido é separado e enviado."}</div>
          <Link to="/produtos" className="btn-ghost w-full">Continuar comprando</Link>
        </div>
      </div>
    </div>
  );
}
